import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { UserService } from './user.service';
import { Store } from '@ngrx/store';
import { loginSuccess, restoreAuthState } from '../state/auth/auth.actions';
import { handleError } from '../shared/helper/helpers-functions';
import { catchError, of } from 'rxjs';
import { NotificationService } from './notification.service';
import { AuthToken } from './interfaces/token.interface';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private url = environment.auth.url;
  private clientId = environment.auth.clientId;
  private baseUrl = environment.baseUrl;

  triggerAuthSignal = signal(false);

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private store: Store,
    private router: Router,
    private notificationService: NotificationService
  ) {}

  triggerAuth() {
    this.triggerAuthSignal.set(true);
  }

  resetTriggerAuth() {
    this.triggerAuthSignal.set(false);
  }

  authenticate() {
    this.userService.logout();

    const currentUrl = window.location.pathname + window.location.search;
    const state = currentUrl !== '/home' ? encodeURIComponent(currentUrl) : '';

    window.location.href =
      this.url +
      '/auth?scope=openid&response_type=code&client_id=' +
      this.clientId +
      '&redirect_uri=' +
      this.baseUrl +
      (state ? '&state=' + state : '');
  }

  retrieveToken(code: string, state?: string) {
    const body = `code=${encodeURIComponent(
      code
    )}&grant_type=authorization_code&client_id=${encodeURIComponent(
      this.clientId
    )}&redirect_uri=${encodeURIComponent(this.baseUrl)}`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };

    const url = `${this.url}/token`;
    return this.http.post<AuthToken>(url, body, httpOptions).pipe(
      map((token) => ({ token, state }))
    );
  }

  refreshToken(refreshToken: string) {
    const body = `refresh_token=${encodeURIComponent(
      refreshToken
    )}&grant_type=refresh_token&client_id=${encodeURIComponent(
      this.clientId
    )}&redirect_uri=${encodeURIComponent(this.baseUrl)}`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };

    const url = `${this.url}/token`;
    return this.http.post<AuthToken>(url, body, httpOptions);
  }

  logoutSession(refreshToken: string) {
    const body = `refresh_token=${encodeURIComponent(
      refreshToken
    )}&client_id=${encodeURIComponent(this.clientId)}`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };

    const url = `${this.url}/logout`;
    return this.http.post<void>(url, body, httpOptions);
  }

  restoreAuthState() {
    const authToken = this.userService.getAccessToken();
    const isAuthenticated = !!authToken;
    this.store.dispatch(restoreAuthState({ isAuthenticated, authToken }));
  }

  storeAuthState(token: AuthToken) {
    this.userService.cacheToken(token);
    this.store.dispatch(loginSuccess({ authToken: token.access_token }));
  }

  isAuthenticated() {
    return this.userService.getAccessToken();
  }

  redirectToHome() {
    this.router.navigate(['/home']);
  }

  logout() {
    const refreshToken = this.userService.getRefreshToken();
    const idToken = this.userService.getIdToken();

    const logoutSession$ = refreshToken
      ? this.logoutSession(refreshToken).pipe(
          catchError((err) => { handleError(
              'logoutSession',
              this.notificationService,
              this.store
            )(err);
            return of(null);
          })
        )
      : of(null);

    logoutSession$.subscribe(() => {
      this.userService.logout();

      const logoutUrl =
        `${this.url}/logout?client_id=${this.clientId}` +
        `&post_logout_redirect_uri=${encodeURIComponent(this.baseUrl)}` +
        (idToken ? `&id_token_hint=${idToken}` : '');

      window.location.href = logoutUrl;
    });
  }
}
